import { useMemo, useState } from "react";
import { Input } from "@baluu/ui/components/input";
import { Label } from "@baluu/ui/components/label";
import { Button } from "@baluu/ui/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@baluu/ui/components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@baluu/ui/components/table";
import {
  Tabs as SubTabs,
  TabsContent as SubTabsContent,
  TabsList as SubTabsList,
  TabsTrigger as SubTabsTrigger,
} from "@baluu/ui/components/tabs";
import { formatCurrency } from "~/widgets/lib/utils";
import { TabSkeleton, TabError } from "../TabStatus";
import { formatDateShort } from "../format";
import type { PurchaseRow } from "../types";
import type { ManageUrl } from "../Profile";

/**
 * Ported from ProfileView.tsx's Purchases tab (:1063-1157, table body
 * :732-870). The hosted version's search/sort are driven by parent-loader
 * state passed in as props (`userSearch`/`sortFilter` + `onSearch`/
 * `onSortFilterChange` actions) — this app's `/widget/me/purchases` fetches
 * every order for the school in one call with no server-side search/sort
 * params (see ../useProfileData.ts), so search + sort are done client-side
 * here on the already-fetched `allOrders` array instead.
 */

type SortField = "creationDate" | "amount";
type SortDir = "asc" | "desc";

function matchesSearch(row: PurchaseRow, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (row.title ?? row.giftcardInfo?.code ?? "").toLowerCase().includes(q);
}

function sortRows(rows: PurchaseRow[], field: SortField, dir: SortDir): PurchaseRow[] {
  const sorted = [...rows].sort((a, b) => {
    if (field === "amount") return a.amount - b.amount;
    return new Date(a.creationDate).getTime() - new Date(b.creationDate).getTime();
  });
  return dir === "desc" ? sorted.reverse() : sorted;
}

function orderUrl(order: PurchaseRow, manageUrl: ManageUrl): string {
  if (order.type === "Booking" || order.type === "Private ") return manageUrl("bookings", order._id);
  if (order.type === "Product") return manageUrl("products", order._id);
  if (order.type === "Bundle") return manageUrl("bundles", order._id);
  return manageUrl("giftcards", order._id);
}

function PurchaseSection({
  items,
  type,
  emptyMessage,
  manageUrl,
}: {
  items: PurchaseRow[];
  type: "bundle" | "booking" | "product" | "giftcard";
  emptyMessage: string;
  manageUrl: ManageUrl;
}) {
  if (items.length === 0) {
    return (
      <div className="rounded-lg border bg-muted/50 p-8 text-center">
        <p className="text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-left">{type === "giftcard" ? "Code" : "Title"}</TableHead>
          <TableHead className="hidden text-left lg:table-cell">
            {type === "bundle" ? "Sessions" : type === "giftcard" ? "Balance" : "Event Date"}
          </TableHead>
          <TableHead className="hidden text-center lg:table-cell">Amount</TableHead>
          <TableHead className="hidden text-center lg:table-cell">{type === "giftcard" ? "Expires" : "Purchase Date"}</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((order) => (
          <TableRow key={order._id}>
            <TableCell>
              <div>
                <div className="font-medium">{type === "giftcard" && order.giftcardInfo ? order.giftcardInfo.code : order.title}</div>
                <div className="text-sm text-muted-foreground md:hidden">
                  {order.eventDate ? formatDateShort(order.eventDate) : ""}
                </div>
              </div>
            </TableCell>
            <TableCell className="hidden lg:table-cell">
              {type === "bundle" && order.sessionsInfo ? (
                <div>
                  <div className="font-medium">{order.sessionsInfo.remaining} remaining</div>
                  <div className="text-sm text-muted-foreground">
                    {order.sessionsInfo.used} of {order.sessionsInfo.total} used
                  </div>
                </div>
              ) : type === "giftcard" && order.giftcardInfo ? (
                <div>
                  <div className="font-medium text-green-600">
                    {formatCurrency(order.giftcardInfo.remainingAmount, order.currencyUsed)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {formatCurrency(order.giftcardInfo.amountUsed, order.currencyUsed)} used
                  </div>
                </div>
              ) : order.eventDate ? (
                formatDateShort(order.eventDate)
              ) : (
                "-"
              )}
            </TableCell>
            <TableCell className="hidden text-center lg:table-cell">
              <span className="font-semibold">{formatCurrency(order.amount, order.currencyUsed)}</span>
            </TableCell>
            <TableCell className="hidden text-center lg:table-cell">
              {type === "giftcard" && order.giftcardInfo?.expiresAt
                ? formatDateShort(order.giftcardInfo.expiresAt)
                : formatDateShort(order.creationDate)}
            </TableCell>
            <TableCell className="text-right">
              <Button variant="outline" size="sm" asChild>
                <a href={orderUrl(order, manageUrl)}>View</a>
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function PurchasesTab({
  allOrders,
  isLoading,
  isError,
  onRetry,
  manageUrl,
}: {
  allOrders: PurchaseRow[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  manageUrl: ManageUrl;
}) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<[SortField, SortDir]>(["creationDate", "desc"]);

  const filtered = useMemo(() => {
    const matched = allOrders.filter((o) => matchesSearch(o, search));
    return sortRows(matched, sort[0], sort[1]);
  }, [allOrders, search, sort]);

  if (isLoading) return <TabSkeleton rows={4} />;
  if (isError) return <TabError onRetry={onRetry} />;

  const bundles = filtered.filter((o) => o.type === "Bundle");
  const bookings = filtered.filter((o) => o.type === "Booking" || o.type === "Private ");
  const products = filtered.filter((o) => o.type === "Product");
  const giftcards = filtered.filter((o) => o.type === "Giftcard");

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-lg border p-4 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Label htmlFor="profile-search" className="mb-2 block">
            Search
          </Label>
          <Input
            id="profile-search"
            placeholder="Search by title"
            defaultValue={search}
            onKeyDown={(e) => {
              if (e.key === "Enter") setSearch(e.currentTarget.value);
            }}
            onBlur={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        <div className="flex-1">
          <Label htmlFor="profile-sort" className="mb-2 block">
            Sort
          </Label>
          <Select
            value={sort.join(":")}
            onValueChange={(v) => setSort(v.split(":") as [SortField, SortDir])}
          >
            <SelectTrigger id="profile-sort" className="w-full">
              <SelectValue />
            </SelectTrigger>
            {/* container=null renders inline instead of portalling to
                document.body — required inside a shadow root, see select.tsx */}
            <SelectContent container={null}>
              <SelectItem value="creationDate:desc">Latest first</SelectItem>
              <SelectItem value="creationDate:asc">Oldest first</SelectItem>
              <SelectItem value="amount:desc">Amount (high - low)</SelectItem>
              <SelectItem value="amount:asc">Amount (low - high)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <SubTabs defaultValue="bundles">
        <SubTabsList className="overflow-x-auto">
          <SubTabsTrigger value="bundles">Bundles ({bundles.length})</SubTabsTrigger>
          <SubTabsTrigger value="bookings">Bookings ({bookings.length})</SubTabsTrigger>
          <SubTabsTrigger value="products">Products ({products.length})</SubTabsTrigger>
          <SubTabsTrigger value="giftcards">Giftcards ({giftcards.length})</SubTabsTrigger>
        </SubTabsList>
        <SubTabsContent value="bundles">
          <PurchaseSection items={bundles} type="bundle" emptyMessage="You haven't purchased any bundles yet." manageUrl={manageUrl} />
        </SubTabsContent>
        <SubTabsContent value="bookings">
          <PurchaseSection items={bookings} type="booking" emptyMessage="You don't have any bookings yet." manageUrl={manageUrl} />
        </SubTabsContent>
        <SubTabsContent value="products">
          <PurchaseSection items={products} type="product" emptyMessage="You haven't purchased any products yet." manageUrl={manageUrl} />
        </SubTabsContent>
        <SubTabsContent value="giftcards">
          <PurchaseSection items={giftcards} type="giftcard" emptyMessage="You haven't purchased any giftcards yet." manageUrl={manageUrl} />
        </SubTabsContent>
      </SubTabs>
    </div>
  );
}
